import { useEffect, useState } from 'react';
import type { ToolCallWithResult } from '../../types';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../ui';
import { cn } from '../../utils';
import {
  Wrench,
  Loader2,
  XCircle,
  MoreHorizontal,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

interface ToolCallProps {
  toolCall: ToolCallWithResult;
  defaultExpanded?: boolean;
  /**
   * Incremented by the parent group each time it is expanded.
   *
   * A counter rather than a boolean because the signal is the EVENT, not a
   * state: the group being open says nothing about whether this row should
   * re-open, since the user may have collapsed it deliberately in the
   * meantime. A new value means "the group was just opened, show everything".
   */
  expandSignal?: number;
}

function formatArguments(args: Record<string, unknown>): string {
  try {
    return JSON.stringify(args, null, 2);
  } catch {
    return String(args);
  }
}

function formatResult(result: ToolCallWithResult['result']): string {
  if (!result) return 'No result';

  const textParts = result.content
    .filter((c) => c.type === 'text' && c.text)
    .map((c) => c.text);

  if (textParts.length > 0) {
    return textParts.join('\n');
  }

  // Fall back to JSON representation
  return JSON.stringify(result, null, 2);
}

/**
 * Status is shown only while it carries information.
 *
 * A completed call needs no badge — the absence of a spinner already says it
 * finished, and a row of green ticks down the transcript is noise competing
 * with the answer the user actually wants to read. Running and error states DO
 * carry information (something is happening / something went wrong), so those
 * stay visible.
 */
/**
 * The row's LEADING glyph: what this is, or what it is doing.
 *
 * One slot, not two. The spinner used to sit at the far right while a wrench
 * held the left, so a running call showed two icons and the eye had to travel
 * the width of the row to learn the state. Occupying the same position means
 * the icon simply *becomes* the spinner while work is in flight and reverts
 * when it settles — the change is where you are already looking.
 *
 * A failure keeps its own glyph: an error is not a state the wrench should
 * quietly return from.
 */
function LeadingIcon({
  status,
  className,
}: {
  status: ToolCallWithResult['status'] | 'mixed';
  className?: string;
}) {
  const cls = cn('h-3.5 w-3.5 flex-shrink-0', className);
  switch (status) {
    case 'running':
      return <Loader2 className={cn(cls, 'animate-spin')} />;
    case 'error':
      return <XCircle className={cls} />;
    case 'mixed':
      // Several states at once — no single glyph is honest, so say so.
      return <MoreHorizontal className={cls} />;
    default:
      return <Wrench className={cls} />;
  }
}

/**
 * The expand affordance, inline after the label.
 *
 * `AccordionTrigger` appends its own chevron at the row's far right and
 * rotates it via `[&[data-state=open]>svg]:rotate-180`, which only matches a
 * DIRECT svg child. Rendering ours nested means the built-in rule cannot see
 * it, so rotation is driven by `data-state` on the trigger via `group-`
 * variants instead, and the built-in one is hidden with `[&>svg:last-child]`.
 *
 * Sitting beside the label rather than across the row keeps the whole control
 * — icon, name, chevron — as one readable unit, instead of three things
 * spread across a line whose width depends on the tool's name.
 */
function ExpandChevron() {
  return (
    <ChevronDown
      className="h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
      aria-hidden="true"
    />
  );
}

/**
 * Collapse many call states into the one the group should show.
 *
 * - any running  → running. Work in progress outranks anything settled; the
 *                  group is not finished until every call is.
 * - any error    → error. A failure must not be hidden behind its siblings'
 *                  success.
 * - mixed        → ellipsis. Only reachable when calls differ in a way the two
 *                  rules above don't resolve; better an honest "several states"
 *                  than picking one and misrepresenting the rest.
 * - all the same → that state (so all-completed shows nothing, as a single
 *                  completed call does).
 */
type AggregateStatus = ToolCallWithResult['status'] | 'mixed';

function aggregateStatus(toolCalls: ToolCallWithResult[]): AggregateStatus {
  const states = new Set(toolCalls.map((tc) => tc.status));
  if (states.size <= 1) {
    return (toolCalls[0]?.status ?? 'completed') as AggregateStatus;
  }
  if (states.has('running')) return 'running';
  if (states.has('error')) return 'error';
  return 'mixed';
}

export function ToolCall({
  toolCall,
  defaultExpanded = false,
  expandSignal,
}: ToolCallProps) {
  const [openValue, setOpenValue] = useState<string | undefined>(
    defaultExpanded ? 'tool' : undefined,
  );
  /**
   * Arguments follow the outer accordion by default, so opening a tool call
   * shows the whole thing in one click.
   *
   * Seeded from `defaultExpanded` and then re-seeded by `onValueChange` below
   * rather than being derived: once the row is open the user can still collapse
   * Arguments on its own, and a derived value would spring it back open on the
   * next render. Expanding the outer row is a fresh "show me this", so it
   * re-opens then — but only then.
   */
  const [isArgsExpanded, setIsArgsExpanded] = useState(defaultExpanded);

  /**
   * Re-open this row (and its Arguments) whenever the GROUP is freshly
   * expanded.
   *
   * `expandSignal` increments on each group expansion, so this effect fires
   * only on a real "show me everything" — not on every render, and not when
   * the group is collapsed. Without it a call the user had collapsed by hand
   * would stay collapsed through later group expansions, and the one-click
   * cascade would silently do nothing for exactly the rows they had been
   * poking at.
   */
  useEffect(() => {
    if (expandSignal === undefined || expandSignal === 0) return;
    setOpenValue('tool');
    setIsArgsExpanded(true);
  }, [expandSignal]);
  const hasResult = toolCall.status === 'completed' || toolCall.status === 'error';
  const resultText = hasResult
    ? toolCall.status === 'error'
      ? toolCall.error || 'Unknown error'
      : formatResult(toolCall.result)
    : null;

  // Truncate result for preview
  const resultPreview = resultText
    ? resultText.length > 200
      ? resultText.slice(0, 200) + '...'
      : resultText
    : null;

  return (
    // Borderless, background-less and uncoloured — supporting detail, not a
    // panel. The failure is still legible once expanded (the error body keeps
    // its red), so the collapsed row doesn't need to shout.
    <div className="rounded-md text-sm overflow-hidden">
      <Accordion
        type="single"
        collapsible
        value={openValue}
        // Opening the row opens its sections too — one click for the full
        // detail instead of one per level. Closing leaves them as they are, so
        // reopening shows what you last had rather than resetting.
        onValueChange={(value) => {
          setOpenValue(value);
          if (value === 'tool') setIsArgsExpanded(true);
        }}
      >
        <AccordionItem value="tool" className="border-0">
          {/* `group` so the nested chevron can read the trigger's data-state;
              `[&>svg:last-child]:hidden` drops AccordionTrigger's own, which is
              anchored to the far right. */}
          <AccordionTrigger className="group px-3 py-1.5 hover:no-underline [&>svg:last-child]:hidden">
            {/* Whole header sits at muted-foreground — the tool name is context
                for the answer, not a heading competing with it. Hovering lifts
                it so the row still reads as interactive. */}
            {/* Uniformly muted — no per-status colour. The icon alone carries
                the state; a spinner reads as "working" and an ✗ as "failed"
                without needing either a label or a hue. */}
            <div className="flex items-center gap-2 flex-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <LeadingIcon status={toolCall.status} />
              <span className="truncate">{toolCall.name}</span>
              <ExpandChevron />
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-3 pb-3 space-y-3">
            {/* Arguments section */}
            <div>
              <button
                onClick={() => setIsArgsExpanded(!isArgsExpanded)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronRight
                  className={cn(
                    'h-3 w-3 transition-transform',
                    isArgsExpanded && 'rotate-90'
                  )}
                />
                Arguments
              </button>
              {isArgsExpanded && (
                <pre className="mt-1 p-2 rounded bg-background/50 text-xs overflow-x-auto">
                  {formatArguments(toolCall.arguments)}
                </pre>
              )}
            </div>

            {/* Result section */}
            {hasResult && (
              <div>
                <span className="text-xs text-muted-foreground">
                  {toolCall.status === 'error' ? 'Error' : 'Result'}
                </span>
                <pre
                  className={cn(
                    'mt-1 p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap break-words',
                    toolCall.status === 'error'
                      ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                      : 'bg-background/50'
                  )}
                >
                  {resultPreview}
                </pre>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

interface ToolCallsProps {
  toolCalls: ToolCallWithResult[];
  defaultExpanded?: boolean;
}

/**
 * All of a turn's tool calls behind ONE collapsed accordion.
 *
 * Previously each call was its own row, so a turn using several tools produced
 * a stack of them ahead of the answer. Grouping means a turn costs exactly one
 * line regardless of how many tools ran, and the header's aggregate status says
 * whether anything still needs attention without opening it.
 *
 * The group header shows a count rather than names — which tools ran is the
 * detail you open it for.
 */
export function ToolCalls({ toolCalls, defaultExpanded = false }: ToolCallsProps) {
  /**
   * Bumped each time the group is expanded, to cascade that down to every call
   * inside it — one click for the whole tree instead of one per level.
   *
   * A counter, not a flag: a call the user collapsed by hand should stay
   * collapsed while the group remains open, but should re-open on the next
   * fresh expansion. Only a new value distinguishes those.
   *
   * Declared BEFORE the empty-list early return: a hook after a conditional
   * return changes hook order when a message goes from having tool calls to
   * having none, which React cannot reconcile.
   */
  const [expandSignal, setExpandSignal] = useState(0);

  if (!toolCalls || toolCalls.length === 0) return null;

  const status = aggregateStatus(toolCalls);

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={defaultExpanded ? 'tools' : undefined}
      // One click, full detail. Opening the group cascades to each call and,
      // through ToolCall's own handler, to its arguments — the alternative was
      // three clicks to answer "what did it actually send?", which is the
      // question the panel exists for.
      onValueChange={(value) => {
        if (value === 'tools') setExpandSignal((n) => n + 1);
      }}
    >
      <AccordionItem value="tools" className="border-0">
        <AccordionTrigger className="group px-3 py-1.5 hover:no-underline [&>svg:last-child]:hidden">
          <div className="flex items-center gap-2 flex-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
            {/* Same slot as a single call's: the group spins while any call is
                still running and settles to the wrench when they all finish. */}
            <LeadingIcon status={status} />
            <span className="truncate">
              {toolCalls.length === 1
                ? '1 tool call'
                : `${toolCalls.length} tool calls`}
            </span>
            <ExpandChevron />
          </div>
        </AccordionTrigger>
        <AccordionContent className="pb-1">
          <div className="space-y-0.5 pl-3">
            {toolCalls.map((tc) => (
              // Cascaded from the group: expanding it opens the calls too, so
              // the whole tree is one click. `key` includes the cascade state
              // so a fresh expansion REMOUNTS each call — otherwise a call the
              // user had collapsed by hand would keep that state forever and
              // the cascade would appear to do nothing.
              <ToolCall
                key={tc.id}
                toolCall={tc}
                defaultExpanded={defaultExpanded}
                expandSignal={expandSignal}
              />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
